import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '../../components/ui';
import { toast } from '../../lib/toast';
import { useOnboardingStatus, useSeedSample, type OnboardingStatus } from './api';

const DISMISS_KEY = 'onboarding-dismissed';

const STEPS: Array<{ key: keyof OnboardingStatus; label: string; to: string }> = [
  { key: 'firmaNastavena', label: 'Vyplň údaje firmy (pro nabídky a PDF)', to: '/nastaveni' },
  { key: 'maZakazku', label: 'Založ první zakázku', to: '/zakazky' },
  { key: 'maVyrobu', label: 'Přidej výrobu a fáze', to: '/zakazky' },
  { key: 'maTym', label: 'Přidej člena týmu', to: '/tym' },
  { key: 'maNabidku', label: 'Vytvoř cenovou nabídku', to: '/zakazky' },
  { key: 'maRecept', label: 'Ulož první recept do Knowledge base', to: '/znalosti' },
];

export default function OnboardingChecklist() {
  const { data } = useOnboardingStatus();
  const seed = useSeedSample();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  if (!data || dismissed) return null;
  const done = STEPS.filter((s) => data[s.key]).length;
  if (done === STEPS.length) return null; // vše hotovo → zmizí

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); };

  return (
    <section className="rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50/60 dark:bg-sky-950/20 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">👋 Začínáme — {done}/{STEPS.length} hotovo</h2>
          <p className="text-xs text-zinc-500">Pár kroků, ať appku rozjedeš. Nebo si načti ukázková data a proklikej hotový příklad.</p>
        </div>
        <button onClick={dismiss} className="text-xs text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200">skrýt</button>
      </div>

      <ul className="space-y-1.5">
        {STEPS.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-sm">
            <span className={data[s.key] ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-300 dark:text-zinc-600'}>
              {data[s.key] ? '✓' : '○'}
            </span>
            {data[s.key] ? (
              <span className="text-zinc-400 line-through">{s.label}</span>
            ) : (
              <Link to={s.to} className="hover:underline">{s.label}</Link>
            )}
          </li>
        ))}
      </ul>

      {!data.hasSample && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="secondary"
            disabled={seed.isPending}
            onClick={() => seed.mutate(undefined, { onSuccess: (r) => toast.success(r.created ? 'Ukázková data načtena' : 'Ukázka už existuje') })}
          >
            {seed.isPending ? 'Načítám…' : '✨ Načíst ukázková data'}
          </Button>
        </div>
      )}
    </section>
  );
}
